import { useState, useEffect } from "react";
// material
import {
  Box,
  Card,
  Typography,
  TableCell,
  TableBody,
  TableContainer,
  Table,
  TableRow,
  TablePagination,
  Stack,
} from "@mui/material";
import moment from "moment";
import _, { isEmpty } from "lodash";
// components
import { Log, sendDataWithGet, sendDataWithPost } from "../../services/services";

import ListHead from "../../components/table/ListHead";
import Scrollbar from "../../components/table/Scrollbar";
import MoreMenu from "../../components/Product-Update/MoreMenu";
import Label from "../../components/Label";
import { applySortFilter, capitalise } from "../../utils/tableFunctions";
import CreateDialog from "../../components/Product-Update/Dialogs/CreateDialog";
import ViewDialog from "../../components/Product-Update/Dialogs/ViewDialog";
import { useLoadingSpinnerContext } from "../../context/loadingSpinnerContext";
import { SERVICE_SCOPE } from "../../utils/constants";
import EditDialog from "../../components/Product-Update/Dialogs/EditDialog";
import CancelDialog from "../../components/Product-Update/Dialogs/CancelDialog";
import PublishDateDialog from "../../components/Product-Update/Dialogs/PublishDateDialog";
import { removeHTMLTags, truncateText } from "../../utils/appUtils";
import { StyledTableRow } from "../../components/TableRow";
import FormSearch from "../../components/FormSearch";
import CircularLoader from "../../components/circularLoader";

// ----------------------------------------------------------------------

const TABLE_HEAD = [
  { id: "service_scope", label: "Service scope" },
  { id: "title", label: "Title" },
  { id: "description", label: "Description" },
  { id: "published_date", label: "Published date", align: "center" },
  { id: "status", label: "Status", align: "center" },
  { id: "action", label: "", align: "center" },
];

const STATUS = ["published", "canceled", "draft"];

let filter = {};

export default function ProductUpdateList({ userRole }) {
  const { setGlobalLoading } = useLoadingSpinnerContext();
  const [loaded, setLoaded] = useState(false);
  const [tableData, setTableData] = useState({
    results: [],
  });

  // ------------------ filterStuffies --------------------//
  const [pagination, setPagination] = useState({ page: 1, page_size: 50 });

  //Dialogs
  const [openEditDialog, setOpenEditDialog] = useState(false);
  const [openViewDialog, setOpenViewDialog] = useState(false);
  const [openPublishDialog, setOpenPublishDialog] = useState(false);
  const [openCancelDialog, setOpenCancelDialog] = useState(false);
  const [selectedId, setSelectedId] = useState();
  const [order, setOrder] = useState("asc");
  const [orderBy, setOrderBy] = useState("name");

  const handleRequestSort = (event, property) => {
    const isAsc = orderBy === property && order === "asc";
    setOrder(isAsc ? "desc" : "asc");
    setOrderBy(property);
  };

  const handleChangePage = (event, newPage) => {
    queryProductUpdateList({
      page: newPage + 1,
      page_size: pagination.page_size,
    });
  };

  const handleChangeRowsPerPage = (event) => {
    queryProductUpdateList({
      page: 1,
      page_size: event.target.value,
    });
  };

  const getProductUpdateById = async (id, cb) => {
    setGlobalLoading(true);
    const url = `/admins/product_update/details/${id}`;
    await sendDataWithGet(url, (res) => cb?.(res?.[0]), null, null, handleCloseDialog);
    setGlobalLoading(false);
  };

  const CustomTableCell = ({ align, field, id, width, sx }) => {
    return (
      <TableCell
        align={align || "center"}
        width={width}
        sx={sx}
        onClick={() => getProductUpdateById(id, setOpenViewDialog)}
      >
        {field}
      </TableCell>
    );
  };

  const queryProductUpdateList = (params) => {
    setLoaded(false);
    queryPage(params);
  };

  const queryPage = (params) => {
    const queryParams = new URLSearchParams(params || pagination).toString();
    const url = `/admins/product_update/filter/?${queryParams}`;
    sendDataWithPost(url, filter, setTableData, setLoaded, () => {
      params && setPagination(params);
    });
  };

  const onFilterSubmit = (fParams) => {
    const { start_datetime, end_datetime, description, title } = fParams || filter || {};
    filter = {
      ...fParams,
      start_datetime: start_datetime && moment(start_datetime).isValid() ? start_datetime.format("YYYY-MM-DD") : null,
      end_datetime:  end_datetime && moment(end_datetime).isValid() ? end_datetime.format("YYYY-MM-DD") : null,
      title: title?.trim(),
      description: description?.trim()
    };
    const newPagination = {
      ...pagination,
      page: 1,
    };
    Log.info("Search Product Update list", filter);
    queryProductUpdateList(newPagination);
  };

  // ------------------ end of filter stuffies --------------------//

  useEffect(() => {
    queryPage();
  }, []);

  const isUserNotFound = tableData?.results?.length === 0;

  const filteredList = applySortFilter(
    tableData?.results || [],
    order,
    orderBy
  );

  const handleCloseDialog = () => {
    openEditDialog && setOpenEditDialog(false);
    openCancelDialog && setOpenCancelDialog(false);
    openPublishDialog && setOpenPublishDialog(false);
    selectedId && setSelectedId();
  };

  const schema = [
    {
      type: "select",
      label: "Service scope",
      param: "service_scope",
      options: SERVICE_SCOPE.map((item) => item),
      id: false
    },
    {
      type: "text",
      label: "Title",
      param: "title"
    },
    {
      type: "text",
      label: "Description",
      param: "description"
    },
    {
      type: "select",
      label: "Status",
      param: "status",
      options: STATUS.map((item) => item),
      id: false
    },
    {
      type: "daterange",
      label: "Published date",
      param: ["start_datetime", "end_datetime"],
      width: 6,
    },
  ]

  return (
    <Box>
      {
        userRole?.includes("Add") && (
          <Stack
            direction="row"
            sx={{
              display: "flex",
              justifyContent: "flex-end",
              mb: 2,
              alignItems: "center",
            }}
          >
            <CreateDialog queryPage={onFilterSubmit} />
          </Stack>
        )
      }
      {openViewDialog && (
        <ViewDialog
          data={openViewDialog}
          handleClose={() => setOpenViewDialog()}
        />
      )}

      <FormSearch
        schema={schema}
        loading={false}
        onSearch={onFilterSubmit}
      />

      {loaded ? (
        <Card>
          <Scrollbar>
            <TableContainer sx={{ maxHeight: "calc(100vh - 400px)" }}>
              <Table stickyHeader sx={{ minWidth: 800 }}>
                <ListHead
                  order={order}
                  orderBy={orderBy}
                  onRequestSort={handleRequestSort}
                  headLabel={TABLE_HEAD}
                  checkbox={false}
                />
                <TableBody>
                  {filteredList?.map((row, i) => {
                    const {
                      id,
                      service_scope,
                      title,
                      description,
                      published_date,
                      status,
                    } = row;

                    return (
                      <StyledTableRow hover key={i} sx={{ cursor: "pointer" }}>
                        <CustomTableCell id={id} align="left" field={service_scope} />
                        <CustomTableCell id={id} align="left" field={title} />
                        <CustomTableCell
                          id={id}
                          align="left"
                          field={truncateText(removeHTMLTags(description), 150)}
                          width={"30%"}
                          sx={{ wordBreak: 'break-all'}}
                        />
                        <CustomTableCell
                          id={id}
                          field={
                            published_date
                              ? moment(published_date).format("DD-MM-YYYY")
                              : "-"
                          }
                        />

                        <CustomTableCell
                          id={id}
                          field={
                            <Label
                              variant="ghost"
                              color={
                                status === "published"
                                  ? "success"
                                  : status === "canceled"
                                  ? "error"
                                  : "primary"
                              }
                            >
                              {capitalise(status)}
                            </Label>
                          }
                        />
                        <TableCell align="right">
                          {status !== "canceled" && (
                            <MoreMenu
                              userRole={userRole}
                              setOpenEditDialog={setOpenEditDialog}
                              setOpenPublishDialog={setOpenPublishDialog}
                              setOpenCancelDialog={setOpenCancelDialog}
                              setRow={() => setSelectedId(id)}
                              status={status}
                            />
                          )}
                        </TableCell>
                      </StyledTableRow>
                    );
                  })}
                </TableBody>
                {isUserNotFound && (
                  <TableBody>
                    <TableRow>
                      <TableCell align="center" colSpan={9} sx={{ py: 3 }}>
                        <Typography
                          gutterBottom
                          align="center"
                          variant="subtitle1"
                        >
                          Not found
                        </Typography>
                      </TableCell>
                    </TableRow>
                  </TableBody>
                )}
              </Table>
            </TableContainer>
          </Scrollbar>
          {!isEmpty(tableData?.results) && (
            <TablePagination
              rowsPerPageOptions={[10, 50, 100]}
              component="div"
              count={tableData.count}
              rowsPerPage={pagination.page_size || 50}
              page={pagination.page - 1 || 0}
              onPageChange={handleChangePage}
              onRowsPerPageChange={handleChangeRowsPerPage}
            />
          )}
        </Card>
      ) : (
        <CircularLoader />
      )}
      {selectedId && (
        <>
          <EditDialog
            open={openEditDialog}
            queryPage={onFilterSubmit}
            selectedId={selectedId}
            getProductUpdateById={getProductUpdateById}
            handleCloseDialog={handleCloseDialog}
          />
          <PublishDateDialog
            open={openPublishDialog}
            selectedId={selectedId}
            queryPage={onFilterSubmit}
            handleCloseDialog={handleCloseDialog}
          />
          <CancelDialog
            open={openCancelDialog}
            selectedId={selectedId}
            queryPage={onFilterSubmit}
            handleCloseDialog={handleCloseDialog}
          />
        </>
      )}
    </Box>
  );
}
